import { NpcLaunchButton } from '@/components/admin/npc/launch/launch-button';
import { useAdminSession } from '@/lib/admin-session';
import { fetchNpcReplays } from '@/lib/api';
import { lastReplayLine } from '@/lib/npc-replays-labels';
import { Button, Icon, buttonVariants, cn } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

/**
 * Ce que la fiche d'un NPC pose au-dessus de son historique (npc.md §12, planche `Fiche`) :
 * « Lancer un rejeu » - qui ouvre la boîte de §6 sur ce NPC - et « Regarder le dernier rejeu »,
 * avec la ligne qui dit lequel c'est. C'est ce que `asideTop` de `NpcEditor` loge.
 *
 * « Lancer » est absent d'un NPC retiré (`npc-retired`) : la boîte n'a rien à lui faire jouer, et le
 * dire serait un refus qu'on peut éviter. « Regarder » reste : un rejeu déjà joué se regarde. Le
 * dernier rejeu est le premier de la liste de l'onglet « Rejeux » (le plus récent d'abord), lue
 * sous la clé que l'onglet écrit : un seul appel pour les deux.
 *
 * Pour un banc entier, on passe par l'onglet Bancs : la boîte d'ici ne lance qu'un NPC.
 */

export interface NpcFicheAsideProps {
  npcId: string;
  name: string;
  retired: boolean;
}

export function NpcFicheAside({ npcId, name, retired }: NpcFicheAsideProps) {
  const { email } = useAdminSession();
  const replays = useQuery({
    queryKey: ['npc-replays', email, npcId],
    queryFn: () => fetchNpcReplays(npcId),
    retry: false,
  });
  const last = replays.data?.replays[0];

  return (
    <section
      data-testid="npc-fiche-lancer"
      aria-label="Lancer et regarder"
      className="space-y-3 rounded-md border-2 border-gray-200 bg-white px-4 py-3.5"
    >
      <div className="flex flex-wrap gap-2">
        {retired ? null : (
          <NpcLaunchButton
            target={{ kind: 'npc', npcId, name }}
            label="Lancer un rejeu"
            variant="secondary"
            data-testid="npc-lancer"
          />
        )}
        {last === undefined ? (
          <Button
            type="button"
            variant="accent"
            size="sm"
            compact
            icon="visibility"
            disabled
            data-testid="npc-regarder-dernier"
          >
            Regarder le dernier rejeu
          </Button>
        ) : (
          <Link
            to="/admin/npc/regarder/$id"
            params={{ id: last.sessionId }}
            data-testid="npc-regarder-dernier"
            className={cn(
              buttonVariants({ variant: 'accent', size: 'sm', compact: true }),
              'no-underline',
            )}
          >
            <Icon name="visibility" size={16} />
            Regarder le dernier rejeu
          </Link>
        )}
      </div>
      <p data-testid="npc-dernier-rejeu" className="text-caption text-gray-600 leading-4">
        {replays.isError
          ? 'Les rejeux de ce NPC n’ont pas pu être lus.'
          : last === undefined
            ? replays.isPending
              ? 'Lecture des rejeux...'
              : 'Aucun rejeu n’a encore joué ce NPC.'
            : lastReplayLine(last)}
      </p>
    </section>
  );
}
